// app/frontend/components/Cards/PrivateLessonPageCard.tsx

import React, { useState, useMemo } from 'react';
import { Card, CardSection, Image, Group, Text, Button, Collapse, Skeleton } from '@mantine/core';
import { IconCalendar, IconChevronDown, IconChevronUp, IconClock, IconMap, IconPointFilled } from '@tabler/icons-react';
import baseImage from '../../assets/images/BASE.jpg';
import { Icon } from 'lucide-react';

interface TimeSlot {
  id: number;
  start_time: string;
  end_time: string;
  available: boolean;
}

interface PrivateLessonPage {
  id: number;
  description?: string;
  event?: { name: string; event_image_url?: string } | null;
  timezone: string;
  slug: string;
  time_slots?: TimeSlot[];
}

interface PrivateLessonPageCardProps {
  page: PrivateLessonPage;
  artistSlug: string;
}

const PrivateLessonPageCard: React.FC<PrivateLessonPageCardProps> = ({ page, artistSlug }) => {
  const [opened, setOpened] = useState(false);

  const groupedSlots = useMemo(() => {
    const groups: Record<string, TimeSlot[]> = {};
    page.time_slots?.forEach((slot) => {
      const date = new Date(slot.start_time).toLocaleDateString();
      if (!groups[date]) groups[date] = [];
      groups[date].push(slot);
    });
    return groups;
  }, [page.time_slots]);

  const hasEventImage = Boolean(page.event?.event_image_url);

  return (
    <Card
      shadow="sm"
      p="md"
      radius="md"
      withBorder
      style={{
        backgroundColor: '#17171750',
        borderColor: '#5c5c5c',
        cursor: 'pointer',
      }}
    >
      {/* Image header with optional overlay text */}
      <CardSection style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
        <Image
          src={page.event?.event_image_url ?? baseImage}
          alt={page.description ?? 'Private Lesson Page'}
          placeholder={<Skeleton width="100%" height="100%" />}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
        {/* Only show description overlay when no event image is present */}
        {!hasEventImage && (
          <Text
            className="rubik"
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: '#ffffff',
              fontSize: '1.2rem',
              width: '80%',
              lineHeight: 1,
              fontWeight: 700,
              textShadow: '0 1px 3px rgba(0, 0, 0, 0.7)',
              textAlign: 'center',
            }}
          >
            {page.description ?? 'Private Lesson Page'}
          </Text>
        )}
      </CardSection>

      {/* Header: description toggle */}
      <Group justify="apart" mt="md" onClick={() => setOpened((o) => !o)}>
        <Text fw={700} className="poppins" style={{ fontSize: 18, color: '#f2f2f2' }}>
          {page.description?.substring(0, 80) || 'No description available'}
        </Text>
        {opened ? <IconChevronUp size={18} color="white" /> : <IconChevronDown color="white" size={18} />}
      </Group>

      {/* Collapsible details */}
      <Collapse in={opened} mt="sm">
        <Group gap="4px" mb="md" align="center">
          <IconMap size={20} color="white" style={{ marginBottom: 3 }} />
          <Text size="sm" className="poppins">
            <strong>Event:</strong> {page.event?.name ?? 'No event associated'}
          </Text>
        </Group>
        <Group gap="4px" mb="md" align="center">
          <IconClock size={20} color="white" style={{ marginBottom: 3 }} />
          <Text size="sm" mt="xs" className="poppins">
            <strong>Timezone:</strong> {page.timezone}
          </Text>
        </Group>
        <Group gap="4px" mb="md" align="center">
          <IconCalendar size={20} color="white" style={{ marginBottom: 3 }} />
          <Text size="sm" mt="xs" className="poppins">
            <strong>Time Slots:</strong> {page.time_slots?.length || 0}
          </Text>
          <Text size="sm" mt="md" className="poppins">
            <strong>Time Slots:</strong>
          </Text>
        </Group>
        <ul style={{ fontFamily: 'Poppins', marginTop: 8, paddingLeft: '1.25rem' }}>
          {Object.entries(groupedSlots).length > 0 ? (
            Object.entries(groupedSlots).map(([date, slots]) => (
              <li key={date}>
                <Group gap="4px">
                  <IconPointFilled size={20} color="white" style={{ marginBottom: 4 }} />
                  <Text component="span" fw={600} className="poppins">
                    {date}
                  </Text>
                </Group>
                <ul style={{ marginTop: 4, paddingLeft: '1rem', color: '#f2f2f290' }}>
                  {slots.map((slot) => (
                    <li key={slot.id} className="poppins">
                      {new Date(slot.start_time).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}{' '}
                      -{' '}
                      {new Date(slot.end_time).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                      : {slot.available ? 'Available' : 'Booked'}
                    </li>
                  ))}
                </ul>
              </li>
            ))
          ) : (
            <li className="poppins">No time slots available.</li>
          )}
        </ul>

        <Button
          component="a"
          href={`/private_lesson_pages/${artistSlug}/${page.slug}`}
          target="_blank"
          variant="filled"
          color="#EA580C"
          mt="xs"
        >
          View Private Lesson Page
        </Button>
      </Collapse>
    </Card>
  );
};

export default PrivateLessonPageCard;
